import type {
  YearPlanResponse,
  TreatmentPlanDto,
  AssignedPatientResponse
} from '../api/generated';
import type { FlatInterventionGroup, GanttGroup, GanttSeries } from '../components/GanttChart/types';

const GROUP_BAR_WIDTH: Record<string, number> = {
  LARGE: 129,
  MEDIUM: 100,
  SMALL: 80
};
const GROUP_BLOCK_GAP = 4;
const CELL_PADDING = 12;
const DEFAULT_COL_WIDTH = 140;

export function flattenGroups(plan: TreatmentPlanDto): FlatInterventionGroup[] {
  return plan.weeks.flatMap((week) =>
    (week.groups ?? []).map((group) => ({
      ...group,
      weekNumber: week.weekNumber ?? 0
    }))
  );
}

export function aggregateAssignedPatients(plan: TreatmentPlanDto): AssignedPatientResponse[] {
  return plan.weeks.flatMap((week) =>
    (week.groups ?? []).flatMap((group) => group.assignedPatients ?? [])
  );
}

// A plan's patients are one cohort moving through its intervention groups, not a
// fresh set of people per group, so summing the groups counted the same cohort
// once per group, and a group repeated across weeks once per week on top of that.
//
// The smallest group rather than the largest: a patient is expected to attend
// every group in the plan, so the narrowest one is the ceiling on how many can
// get through it. A group left without a capacity therefore carries the plan to
// zero, and a plan with no groups carries nobody.
//
// Capacity is validated per group against that group's own bookings, so this can
// read lower than the number of patients actually booked across the plan.
export function planPatientCount(plan: TreatmentPlanDto): number {
  const capacities = plan.weeks.flatMap((week) =>
    (week.groups ?? []).map((group) => group.capacity ?? 0)
  );
  if (capacities.length === 0) return 0;
  return Math.min(...capacities);
}

export function buildGridTemplateColumns(weekWidths: number[]): string {
  return `230px ${weekWidths.map((w) => `${String(w)}px`).join(' ')}`;
}

function groupSeriesGroupsByWeek(series: GanttSeries): Map<number, FlatInterventionGroup[]> {
  const weekMap = new Map<number, FlatInterventionGroup[]>();
  for (const group of series.groups) {
    const weekNumber = group.weekNumber;
    const existing = weekMap.get(weekNumber);
    if (existing) existing.push(group);
    else weekMap.set(weekNumber, [group]);
  }
  return weekMap;
}

function computeSlotWidth(groupsInWeek: FlatInterventionGroup[]): number {
  const blocksWidth = groupsInWeek.reduce(
    (sum, group) => sum + (GROUP_BAR_WIDTH[group.barSize ?? 'LARGE'] ?? 100),
    0
  );
  return blocksWidth + (groupsInWeek.length - 1) * GROUP_BLOCK_GAP + CELL_PADDING;
}

function applySeriesWidths(series: GanttSeries, weekWidths: number[], totalWeeks: number): void {
  for (const [week, groupsInWeek] of groupSeriesGroupsByWeek(series)) {
    const idx = week - 1;
    if (idx < 0 || idx >= totalWeeks) continue;
    const slotWidth = computeSlotWidth(groupsInWeek);
    if (slotWidth > weekWidths[idx]) weekWidths[idx] = slotWidth;
  }
}

export function computePerWeekColumnWidths(groups: GanttGroup[], totalWeeks: number): number[] {
  const weekWidths = new Array(totalWeeks).fill(DEFAULT_COL_WIDTH) as number[];

  for (const ganttGroup of groups) {
    for (const series of ganttGroup.series) {
      applySeriesWidths(series, weekWidths, totalWeeks);
    }
  }

  return weekWidths;
}

interface PlanEntry {
  plan: TreatmentPlanDto;
  originYear?: number;
}

export function buildGanttGroups(yearPlan: YearPlanResponse): GanttGroup[] {
  const carriedOver = yearPlan.carriedOverPlans;
  // The year's own plans go first, so a carried-over plan lands behind them in
  // its template row and never shifts a native series label.
  const entries: PlanEntry[] = [
    ...yearPlan.treatmentPlans.map((plan) => ({ plan })),
    ...(carriedOver
      ? carriedOver.treatmentPlans.map((plan) => ({
          // The previous year's own weeks (0 and below) are for the drawer only.
          plan: { ...plan, weeks: plan.weeks.filter((w) => (w.weekNumber ?? 0) >= 1) },
          originYear: carriedOver.year
        }))
      : [])
  ];

  const grouped = new Map<string, PlanEntry[]>();

  for (const entry of entries) {
    const templateId = entry.plan.treatmentPlanTemplateId;
    const existing = grouped.get(templateId);
    if (existing) {
      existing.push(entry);
    } else {
      grouped.set(templateId, [entry]);
    }
  }

  const result: GanttGroup[] = [];

  for (const [templateId, templateEntries] of grouped) {
    result.push({
      templateId,
      templateName: templateEntries[0].plan.name,
      series: templateEntries.map(({ plan, originYear }, i) => ({
        planIndex: i,
        treatmentPlanId: plan.id,
        groups: flattenGroups(plan),
        seriesLabel: `M${String(i + 1)}`,
        strokeColor: plan.strokeColor,
        fillColor: plan.fillColor,
        highlightColor: plan.highlightColor,
        originYear
      }))
    });
  }

  return result;
}

export function packSeriesIntoRows(
  series: GanttSeries[]
): Map<number, number> {
  const assignment = new Map<number, number>();

  const ranges: { planIndex: number; min: number; max: number }[] = series.map((s) => {
    let min = Infinity;
    let max = -Infinity;
    for (const g of s.groups) {
      const w = g.weekNumber;
      if (w < min) min = w;
      if (w > max) max = w;
    }
    return { planIndex: s.planIndex, min, max };
  });

  const rows: number[] = [];

  for (const range of ranges) {
    if (range.min === Infinity) {
      assignment.set(range.planIndex, rows.length);
      rows.push(-Infinity);
      continue;
    }

    let placed = false;
    for (let r = 0; r < rows.length; r++) {
      if (rows[r] < range.min) {
        assignment.set(range.planIndex, r);
        rows[r] = range.max;
        placed = true;
        break;
      }
    }

    if (!placed) {
      assignment.set(range.planIndex, rows.length);
      rows.push(range.max);
    }
  }

  return assignment;
}

// The series in a row that this year's plan owns. A plan carried over from the
// previous year is drawn in the row but belongs to that year, so no edit made
// here may reach it.
export function collectEditableSeries(group: GanttGroup): GanttSeries[] {
  return group.series.filter((s) => s.originYear === undefined);
}

// Every rolled-out treatment plan of this year sitting in one year-view row.
// Rename and remove act on the row, so they act on all of these.
export function collectGroupTreatmentPlanIds(group: GanttGroup): string[] {
  return collectEditableSeries(group).map((s) => s.treatmentPlanId);
}

// Distinct patients booked into a row, shadow seats excluded: the removal
// warning concerns real patients only.
export function collectGroupPatientIds(group: GanttGroup): string[] {
  const ids = new Set<string>();
  for (const series of group.series) {
    for (const g of series.groups) {
      for (const p of g.assignedPatients ?? []) {
        if (!p.shadow) ids.add(p.patientId);
      }
    }
  }
  return [...ids];
}
